// Control PIN set-up before the shell (UC-IDENTITY-001 A2, UC-IDENTITY-006 A1, BR-IDENTITY-027): first PIN, or a new PIN
// after an in-person reset (the one-time PIN goes in as `resetPin`).
import { PIN_MAX_LENGTH, pinProblem } from "@visionx/contracts";
import { useState, type FormEvent } from "react";
import { auth } from "../api/endpoints";
import { describeError, isApiError } from "../api/problem";
import { formatTime } from "../lib/format";
import { loadSession, signOut, useSession } from "../session/store";
import { Alert, Field, TextInput } from "../ui/bits";
import { Mark } from "../ui/icons";

export function PinSetup() {
  const s = useSession();
  const session = s.status === "pin_setup" ? s.session : null;
  const minLength = session?.settings.pinMinLength ?? 4;
  const [resetPin, setResetPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  // The refusal of the one-time PIN goes under its own field, not under the new PIN's (UC-IDENTITY-006 E1; testing of 12 Oct 2026, finding 39).
  const [resetError, setResetError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldError(null);
    setResetError(null);
    const problem = pinProblem(newPin, { minLength, controlId: session?.user.controlId });
    if (problem) { setFieldError(problem === "trivial" ? "Too easy to guess: no repeated digits, runs like 1234, or your Control ID." : `Use ${minLength} to ${PIN_MAX_LENGTH} digits.`); return; }
    if (newPin !== confirm) { setFieldError("The two PINs do not match."); return; }
    setBusy(true);
    try {
      await auth.changePin({ newPin, ...(resetPin ? { resetPin } : {}) });
      await loadSession();
    } catch (err) {
      if (isApiError(err) && err.code === "VALIDATION_FAILED") {
        const fresh = err.field("newPin");
        const old = err.field("resetPin") ?? err.field("currentPin");
        if (fresh) setFieldError(fresh.message);
        if (old) setResetError(old.message);
        if (!fresh && !old) setFieldError(describeError(err));
      } else if (isApiError(err) && err.code === "STEP_UP_LOCKED") {
        const until = typeof err.meta.lockedUntil === "string" ? formatTime(err.meta.lockedUntil) : null;
        setError(`This Control ID is locked${until ? ` until ${until}` : ""}. Ask an administrator to clear the lock.`);
      } else {
        setError(describeError(err));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="screen" id="s-signin" data-bare="true">
      <div className="content">
        <div className="wrap">
          <div className="cards">
            <div className="stack card">
              <form className="panel" onSubmit={(e) => void submit(e)}>
                <div className="panel-body" style={{ padding: 24, gap: 16 }}>
                  <div className="logo"><Mark className="vxmark" /><span>Vision X</span></div>
                  <div className="stack gap-4">
                    <h1>Set your Control PIN</h1>
                    <div className="help">{session ? <>{session.user.displayName} · Control ID <span className="mono">{session.user.controlIdDisplay}</span>. </> : null}The PIN authorises sensitive actions and unlocks the screen. It is never shown or emailed.</div>
                  </div>
                  {error && <Alert tone="crit">{error}</Alert>}
                  <Field label="One-time PIN from your administrator" help="Only if an administrator reset your PIN in person" htmlFor="pin-reset" error={resetError ?? undefined}>
                    <TextInput id="pin-reset" type="password" inputMode="numeric" pattern="[0-9]*" autoComplete="one-time-code" maxLength={PIN_MAX_LENGTH} value={resetPin} onChange={(e) => setResetPin(e.target.value.replace(/\D/g, ""))} mono />
                  </Field>
                  <Field label="New Control PIN" required htmlFor="pin-new" error={fieldError ?? undefined} help={`${minLength} to ${PIN_MAX_LENGTH} digits. Not all the same digit, not a run like 1234 or 4321, not your Control ID.`}>
                    <TextInput id="pin-new" type="password" inputMode="numeric" pattern="[0-9]*" autoComplete="new-password" maxLength={PIN_MAX_LENGTH} required value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))} mono autoFocus />
                  </Field>
                  <Field label="Repeat the new PIN" required htmlFor="pin-confirm">
                    <TextInput id="pin-confirm" type="password" inputMode="numeric" pattern="[0-9]*" autoComplete="new-password" maxLength={PIN_MAX_LENGTH} required value={confirm} onChange={(e) => setConfirm(e.target.value.replace(/\D/g, ""))} mono />
                  </Field>
                  <button className="btn primary full" type="submit" disabled={busy}>{busy ? "Saving…" : "Save PIN and continue"}</button>
                  <div className="centre"><button className="linkbtn small" type="button" onClick={() => void signOut()}>Sign out</button></div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
